/**
 * Subject-first federated search — pure ranking + result contract.
 * Sources are assembled by `useInsightsSearch`; this module never fetches.
 */

export type InsightsSearchGroup = "subjects" | "knowledge" | "people_orgs" | "work" | "commands";

export type InsightsSearchType =
  | "subject"
  | "map"
  | "vault_document"
  | "workspace"
  | "workspace_document"
  | "note"
  | "contact"
  | "company"
  | "project"
  | "meeting"
  | "command";

export type InsightsSearchSource =
  | "graph_nodes"
  | "graph_maps"
  | "vault"
  | "workspaces"
  | "notes"
  | "contacts"
  | "companies"
  | "projects"
  | "meetings"
  | "nav";

export type InsightsRankReason =
  | "exact_canonical_label"
  | "canonical_prefix"
  | "canonical_alias"
  | "exact_title"
  | "title_prefix"
  | "title_contains"
  | "command";

export type InsightsDestination = {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, string | boolean | undefined>;
};

export type InsightsSearchResult = {
  id: string;
  type: InsightsSearchType;
  title: string;
  description?: string;
  subjectId?: string;
  canonical: boolean;
  source: InsightsSearchSource;
  destination: InsightsDestination;
  relevance: number;
  group: InsightsSearchGroup;
  rankReason: InsightsRankReason;
  metadata?: Record<string, string>;
};

export type InsightsSearchBundle = {
  nodes: Array<{
    id: string;
    label: string;
    kind: string;
    aliases: string[];
    status: string;
  }>;
  maps: Array<{ id: string; title: string; kind: string; visibility: string }>;
  vaultDocuments: Array<{ id: string; folderId: string; title: string; filename: string }>;
  workspaces: Array<{ id: string; name: string; kind: string; isPersonal: boolean }>;
  workspaceDocuments: Array<{ id: string; workspaceId: string; title: string }>;
  projects: Array<{ id: string; name: string }>;
  companies: Array<{ id: string; name: string }>;
  contacts: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    honorific?: string | null;
  }>;
  notes: Array<{ id: string; title: string; kind: string }>;
  meetings: Array<{ id: string; title: string }>;
  commands: Array<{ id: string; label: string; href: string; keywords?: string[] }>;
};

export type InsightsSearchGroupView = {
  group: InsightsSearchGroup;
  heading: string;
  items: InsightsSearchResult[];
};

export const INSIGHTS_SEARCH_GROUP_HEADINGS: Record<InsightsSearchGroup, string> = {
  subjects: "Subjects",
  knowledge: "Knowledge & documents",
  people_orgs: "People & organisations",
  work: "Work & activity",
  commands: "Commands",
};

export const INSIGHTS_SEARCH_TYPE_LABEL: Record<InsightsSearchType, string> = {
  subject: "Subject",
  map: "Map",
  vault_document: "Vault document",
  workspace: "Workspace",
  workspace_document: "Working document",
  note: "Note",
  contact: "Contact",
  company: "Company",
  project: "Project",
  meeting: "Meeting",
  command: "Command",
};

/** Accessible name that distinguishes a Subject hit from “Ask Insights about …”. */
export function insightsSearchResultAccessibleName(
  result: Pick<InsightsSearchResult, "title" | "type">,
): string {
  return `${result.title}, ${INSIGHTS_SEARCH_TYPE_LABEL[result.type]}`;
}

const GROUP_ORDER: InsightsSearchGroup[] = [
  "subjects",
  "knowledge",
  "people_orgs",
  "work",
  "commands",
];

const LIMIT_SUBJECTS = 12;
const LIMIT_OTHER = 8;

const SCORE = {
  exact_canonical_label: 100,
  canonical_prefix: 92,
  exact_title: 84,
  canonical_alias: 76,
  title_prefix_knowledge: 70,
  title_prefix_other: 60,
  title_contains: 45,
  command_exact: 40,
  command_prefix: 30,
  command_contains: 25,
} as const;

function norm(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function tokens(value: string): string[] {
  return norm(value)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function tokenPrefixHit(haystack: string, q: string): boolean {
  return tokens(haystack).some((token) => token === q || token.startsWith(q));
}

type Scored = { relevance: number; rankReason: InsightsRankReason };

function scoreCanonical(label: string, aliases: string[], q: string): Scored | null {
  const hay = norm(label);
  if (hay === q)
    return { relevance: SCORE.exact_canonical_label, rankReason: "exact_canonical_label" };
  if (q.length >= 2 && (hay.startsWith(q) || tokenPrefixHit(label, q))) {
    return { relevance: SCORE.canonical_prefix, rankReason: "canonical_prefix" };
  }
  const aliasHit = aliases.some((alias) => {
    const a = norm(alias);
    if (a === q) return true;
    return q.length >= 2 && (a.startsWith(q) || tokenPrefixHit(alias, q));
  });
  if (aliasHit) return { relevance: SCORE.canonical_alias, rankReason: "canonical_alias" };
  if (q.length >= 2 && hay.includes(q)) {
    return { relevance: SCORE.title_contains, rankReason: "title_contains" };
  }
  return null;
}

function scoreTitle(
  title: string,
  q: string,
  group: Exclude<InsightsSearchGroup, "subjects" | "commands">,
): Scored | null {
  const hay = norm(title);
  if (hay === q) return { relevance: SCORE.exact_title, rankReason: "exact_title" };
  const prefixScore =
    group === "knowledge" ? SCORE.title_prefix_knowledge : SCORE.title_prefix_other;
  if (q.length >= 2 && (hay.startsWith(q) || tokenPrefixHit(title, q))) {
    return { relevance: prefixScore, rankReason: "title_prefix" };
  }
  if (q.length >= 2 && hay.includes(q)) {
    return { relevance: SCORE.title_contains, rankReason: "title_contains" };
  }
  return null;
}

function betterScore(a: Scored | null, b: Scored | null): Scored | null {
  if (!a) return b;
  if (!b) return a;
  return b.relevance > a.relevance ? b : a;
}

function scoreCommand(
  label: string,
  href: string,
  keywords: string[] | undefined,
  q: string,
): Scored | null {
  const hay = norm(label);
  if (hay === q) return { relevance: SCORE.command_exact, rankReason: "command" };
  if (q.length < 2) return null;
  if (hay.startsWith(q) || tokenPrefixHit(label, q)) {
    return { relevance: SCORE.command_prefix, rankReason: "command" };
  }
  const kw = (keywords ?? []).some((word) => {
    const w = norm(word);
    return w === q || w.startsWith(q) || w.includes(q);
  });
  if (kw || hay.includes(q) || norm(href).includes(q)) {
    return { relevance: SCORE.command_contains, rankReason: "command" };
  }
  return null;
}

function cmpResults(a: InsightsSearchResult, b: InsightsSearchResult): number {
  if (b.relevance !== a.relevance) return b.relevance - a.relevance;
  const gi = GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group);
  if (gi !== 0) return gi;
  return a.title.localeCompare(b.title);
}

function takeByType(results: InsightsSearchResult[]): InsightsSearchResult[] {
  const counts = new Map<InsightsSearchType, number>();
  const out: InsightsSearchResult[] = [];
  for (const item of results) {
    const cap = item.type === "subject" ? LIMIT_SUBJECTS : LIMIT_OTHER;
    const used = counts.get(item.type) ?? 0;
    if (used >= cap) continue;
    counts.set(item.type, used + 1);
    out.push(item);
  }
  return out;
}

/** Assemble and rank federated hits. Length is the source-mapper, not extra behaviour. */
/* eslint-disable-next-line max-lines-per-function, complexity -- one mapper per authorised source */
export function buildInsightsSearchResults(
  bundle: InsightsSearchBundle,
  query: string,
): InsightsSearchResult[] {
  const q = norm(query);
  if (!q) return [];
  const raw: InsightsSearchResult[] = [];

  for (const node of bundle.nodes) {
    if (node.status !== "active") continue;
    const scored = scoreCanonical(node.label, node.aliases, q);
    if (!scored) continue;
    raw.push({
      id: `subject:${node.id}`,
      type: "subject",
      title: node.label,
      description: node.kind,
      subjectId: node.id,
      canonical: true,
      source: "graph_nodes",
      destination: { to: "/subject/$subjectId", params: { subjectId: node.id } },
      group: "subjects",
      metadata: { kind: node.kind },
      ...scored,
    });
  }

  for (const map of bundle.maps) {
    const scored = scoreTitle(map.title, q, "knowledge");
    if (!scored) continue;
    raw.push({
      id: `map:${map.id}`,
      type: "map",
      title: map.title,
      description: map.visibility === "private" ? "Private map" : map.kind,
      canonical: false,
      source: "graph_maps",
      destination: { to: "/ideation/maps/$mapId", params: { mapId: map.id } },
      group: "knowledge",
      metadata: { kind: map.kind },
      ...scored,
    });
  }

  for (const doc of bundle.vaultDocuments) {
    const scored = betterScore(
      scoreTitle(doc.title, q, "knowledge"),
      scoreTitle(doc.filename, q, "knowledge"),
    );
    if (!scored) continue;
    raw.push({
      id: `vault_document:${doc.id}`,
      type: "vault_document",
      title: doc.title,
      description: doc.filename,
      canonical: false,
      source: "vault",
      destination: {
        to: "/documents",
        search: { folderId: doc.folderId, q: doc.title },
      },
      group: "knowledge",
      metadata: { origin: "Vault" },
      ...scored,
    });
  }

  for (const ws of bundle.workspaces) {
    const scored = scoreTitle(ws.name, q, "knowledge");
    if (!scored) continue;
    raw.push({
      id: `workspace:${ws.id}`,
      type: "workspace",
      title: ws.name,
      description: ws.isPersonal ? "Personal" : ws.kind,
      canonical: false,
      source: "workspaces",
      destination: ws.isPersonal
        ? { to: "/my-vault", search: { workspaceId: ws.id } }
        : { to: "/documents", search: { mode: "working", workspaceId: ws.id } },
      group: "knowledge",
      metadata: { origin: "Working" },
      ...scored,
    });
  }

  for (const doc of bundle.workspaceDocuments) {
    const scored = scoreTitle(doc.title, q, "knowledge");
    if (!scored) continue;
    raw.push({
      id: `workspace_document:${doc.id}`,
      type: "workspace_document",
      title: doc.title,
      canonical: false,
      source: "workspaces",
      destination: {
        to: "/documents",
        search: { mode: "working", workspaceId: doc.workspaceId },
      },
      group: "knowledge",
      metadata: { origin: "Working document" },
      ...scored,
    });
  }

  for (const note of bundle.notes) {
    const scored = scoreTitle(note.title, q, "knowledge");
    if (!scored) continue;
    raw.push({
      id: `note:${note.id}`,
      type: "note",
      title: note.title,
      description: note.kind,
      canonical: false,
      source: "notes",
      destination: { to: "/notes/$noteId", params: { noteId: note.id } },
      group: "knowledge",
      ...scored,
    });
  }

  for (const company of bundle.companies) {
    const scored = scoreTitle(company.name, q, "people_orgs");
    if (!scored) continue;
    raw.push({
      id: `company:${company.id}`,
      type: "company",
      title: company.name,
      canonical: false,
      source: "companies",
      destination: { to: "/companies/$companyId", params: { companyId: company.id } },
      group: "people_orgs",
      metadata: { origin: "Company" },
      ...scored,
    });
  }

  for (const contact of bundle.contacts) {
    const title = [contact.honorific, contact.firstName, contact.lastName]
      .filter((part) => Boolean(part && String(part).trim()))
      .join(" ");
    const scored = betterScore(
      scoreTitle(title, q, "people_orgs"),
      scoreTitle(contact.email, q, "people_orgs"),
    );
    if (!scored) continue;
    raw.push({
      id: `contact:${contact.id}`,
      type: "contact",
      title: title || contact.email,
      description: contact.email,
      canonical: false,
      source: "contacts",
      destination: { to: "/contacts/$contactId", params: { contactId: contact.id } },
      group: "people_orgs",
      metadata: { origin: "Person" },
      ...scored,
    });
  }

  for (const project of bundle.projects) {
    const scored = scoreTitle(project.name, q, "work");
    if (!scored) continue;
    raw.push({
      id: `project:${project.id}`,
      type: "project",
      title: project.name,
      canonical: false,
      source: "projects",
      destination: { to: "/projects/$projectId", params: { projectId: project.id } },
      group: "work",
      metadata: { origin: "Project" },
      ...scored,
    });
  }

  for (const meeting of bundle.meetings) {
    const scored = scoreTitle(meeting.title, q, "work");
    if (!scored) continue;
    raw.push({
      id: `meeting:${meeting.id}`,
      type: "meeting",
      title: meeting.title,
      canonical: false,
      source: "meetings",
      destination: { to: "/meetings" },
      group: "work",
      metadata: { origin: "Recording" },
      ...scored,
    });
  }

  for (const command of bundle.commands) {
    const scored = scoreCommand(command.label, command.href, command.keywords, q);
    if (!scored) continue;
    raw.push({
      id: `command:${command.id}`,
      type: "command",
      title: command.label,
      canonical: false,
      source: "nav",
      destination: { to: command.href },
      group: "commands",
      ...scored,
    });
  }

  raw.sort(cmpResults);
  return takeByType(raw);
}

export function groupInsightsResults(results: InsightsSearchResult[]): InsightsSearchGroupView[] {
  return GROUP_ORDER.flatMap((group) => {
    const items = results.filter((item) => item.group === group);
    if (items.length === 0) return [];
    return [{ group, heading: INSIGHTS_SEARCH_GROUP_HEADINGS[group], items }];
  });
}

export function insightsAskInsightsDestination(query: string): InsightsDestination {
  return { to: "/ai", search: { q: query.trim() } };
}
